/* eslint-disable security/detect-object-injection */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    ArrayExtensions,
    getProductAvailabilitiesForSelectedVariant,
    getSimpleProductsAction,
    ProductAvailabilitiesForSelectedVariantInput,
    ProductInput
} from '@msdyn365-commerce-modules/retail-actions';
import {
    ICategoryHierarchyViewProps,
    IRefineMenuViewProps,
    ISearchResultContainerViewProps,
    ISearchResultModalViewProps,
    ISortByViewProps,
    ITitleViewProps
} from '@msdyn365-commerce-modules/search-result-container';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { AddToCartComponent } from '@msdyn365-commerce/components';
import MsDyn365, { ICoreContext } from '@msdyn365-commerce/core';
import { AsyncResult, DataValidationFailure, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { toJS } from 'mobx';
import React, { useEffect, useState } from 'react';
import { Toaster } from 'sonner';
import { Availability } from '../../actions/get-product-availabilities-for-selected-variant.action';
import IncrementalQuantity from '../../themes/master/views/components/incrementalQuantity';
import ProductPopover from '../../themes/master/views/components/product-popover.component';
import { DEFAULT_MAX_QUANTITY } from '../../utilities/quantity-constants';
import { _showToast } from '../../utilities/showToastMessage';
import { fetchVArticleMap } from '../../utilities/v-article-ids';
import { formatPrice } from './components';
import { ISearchResultContainerProps, layout } from './search-result-container.props.autogenerated';

interface errorMessage {
    LocalizedMessageParameters: string;
}

/**
 * Render products in a table view.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProductsAsTable = (props: ISearchResultContainerViewProps & ISearchResultContainerProps<{}>): JSX.Element | null => {
    const products = props.data.listPageState.result?.activeProducts;
    const { context, typeName, id, resources } = props;
    const [quantities, setQuantities] = useState<number[]>(new Array(products?.length).fill(1));
    const [availabilities, setAvailabilities] = useState(Array(products?.length).fill(Availability.loading));
    const [FullProducts, setFullProducts] = useState<SimpleProduct[]>([]);
    const [surfaceFinishingValues, setSurfaceFinishingValues] = useState<string[]>(new Array(products?.length).fill(''));
    const [surfaceFinishingDisabled, setSurfaceFinishingDisabled] = useState<boolean[]>(new Array(products?.length).fill(true));

    const handleQuantityChange = (index: number, value: number) => {
        setQuantities(prevQuantities => {
            const newQuantities = [...prevQuantities];
            newQuantities[index] = value < 1 ? 1 : value; // Ensure quantity is at least 1
            return newQuantities;
        });
    };

    const handleSurfaceFinishingChange = (index: number, value: string) => {
        const updateSurfaceValues = [...surfaceFinishingValues];
        updateSurfaceValues[index] = value;
        setSurfaceFinishingValues(updateSurfaceValues);
    };

    const isList = props.config.layout === layout.list;
    const isImageList = props.config.layout === layout.imagelist;

    const _getProducts = (productList: ProductSearchResult[] = []): Promise<SimpleProduct[]> => {
        const productInputs = productList
            .filter(product => product.RecordId)
            .map(product => new ProductInput(product.RecordId, props.context.request.apiSettings));
        return getSimpleProductsAction(productInputs, props.context.actionContext);
    };

    useEffect(() => {
        if (!products || !ArrayExtensions.hasElements(products)) {
            return;
        }

        const fetchProducts = async () => {
            const simpleProducts = await _getProducts(products);
            setFullProducts(simpleProducts);

            simpleProducts.map((p: SimpleProduct, i) => {
                const minQ = p.Behavior?.MinimumQuantity ?? 0;
                const mulQ = p.Behavior?.MultipleOfQuantity ?? 0;
                const qty = minQ > 0 ? minQ : mulQ > 0 ? mulQ : 1;
                handleQuantityChange(i, qty);
            });

            const ids = simpleProducts.map(p => String(p?.ItemId ?? '')).filter(Boolean);
            if (ids.length > 0) {
                try {
                    const map = await fetchVArticleMap(ids, context.actionContext);
                    const surfaceDisabledPerRow = products.map((_, i) => {
                        const id = String(simpleProducts[i]?.ItemId ?? '');
                        return id ? !map[id] : true;
                    });
                    setSurfaceFinishingDisabled(surfaceDisabledPerRow);
                } catch {
                    setSurfaceFinishingDisabled(products.map(() => true));
                }
            } else {
                setSurfaceFinishingDisabled(products.map(() => true));
            }
        };

        const fetchAvailabilities = async () => {
            for (let index = 0; index < products.length; index++) {
                const product = products[index];
                if (product) {
                    const availability = await fetchAvailableQuantity(product, context);
                    setAvailabilities(prevAvailabilities => {
                        const updatedAvailabilities = [...prevAvailabilities];
                        updatedAvailabilities[index] =
                            availability?.PhysicalAvailableInventoryLevelLabel === Availability.available
                                ? Availability.available
                                : Availability.outOfStock;
                        return updatedAvailabilities;
                    });
                }
            }
        };

        void fetchProducts();
        void fetchAvailabilities();
    }, [products, context]);

    if (!products || !ArrayExtensions.hasElements(products)) {
        return null;
    }

    const currencyCode = ((context.cultureFormatter as unknown) as { currencyCode?: string })?.currencyCode;
    const locale = ((context.cultureFormatter as unknown) as { locale?: string })?.locale;
    const isAuthenticated = props.context.request.user.isAuthenticated;

    return (
        <>
            <Toaster
                richColors
                position='top-right'
                toastOptions={{
                    classNames: {
                        error: 'toast-danger'
                    }
                }}
            />
            <Node {...props.ProductsContainer}>
                {props.errorMessage}
                <div className='table-responsive'>
                    <table className='ms-search-result-container__product-table table table-sm'>
                        <tbody>
                            {products.map((product: ProductSearchResult, index: number) => {
                                const simpleProduct: SimpleProduct = {
                                    RecordId: product.RecordId,
                                    Price: product.Price,
                                    ProductTypeValue: 0,
                                    BasePrice: product.BasePrice || 0,
                                    AdjustedPrice: product.Price
                                };
                                const fullProduct = FullProducts.find(fp => fp.RecordId === product.RecordId);
                                const maxQuantity =
                                    fullProduct?.Behavior?.MaximumQuantity && fullProduct.Behavior.MaximumQuantity > 0
                                        ? fullProduct.Behavior.MaximumQuantity
                                        : DEFAULT_MAX_QUANTITY;
                                const sfValue = surfaceFinishingValues[index] ?? '';
                                if (sfValue && sfValue.trim().length > 0) {
                                    // // @ts-expect-error CommerceProperty typing
                                    simpleProduct.ExtensionProperties = [
                                        {
                                            Key: 'SurfaceFinishing',
                                            Value: { StringValue: sfValue }
                                        }
                                    ];
                                } else {
                                    simpleProduct.ExtensionProperties = [];
                                }
                                return (
                                    <tr key={index}>
                                        <td>
                                            <ProductPopover
                                                product={product}
                                                index={index}
                                                context={context}
                                                layout={isList ? 'list' : isImageList ? 'imagelist' : ''}
                                            />
                                        </td>
                                        {props.config.enableSurfaceFinishing && (
                                            <td>
                                                <input
                                                    className='ms-search-result__surface-finishing'
                                                    type='text'
                                                    disabled={surfaceFinishingDisabled[index]}
                                                    placeholder={resources.addSurfaceFinishingText}
                                                    value={surfaceFinishingValues[index] ?? ''}
                                                    onChange={e => handleSurfaceFinishingChange(index, e.currentTarget.value)}
                                                />
                                            </td>
                                        )}
                                        {isAuthenticated ? (
                                            <td>
                                                {simpleProduct.BasePrice !== simpleProduct.Price ? (
                                                    <>
                                                        <span className='ms-search-result__price-strikethrough'>
                                                            {formatPrice(
                                                                simpleProduct.BasePrice.toString(),
                                                                currencyCode,
                                                                locale,
                                                                context.telemetry
                                                            )}
                                                        </span>{' '}
                                                        <span className='ms-search-result__price-current'>
                                                            {formatPrice(
                                                                simpleProduct.Price.toString(),
                                                                currencyCode,
                                                                locale,
                                                                context.telemetry
                                                            )}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className='ms-search-result__price-current'>
                                                        {formatPrice(
                                                            simpleProduct.Price.toString(),
                                                            currencyCode,
                                                            locale,
                                                            context.telemetry
                                                        )}
                                                    </span>
                                                )}
                                            </td>
                                        ) : (
                                            <td></td>
                                        )}
                                        <td>
                                            {FullProducts.length > 0 && (
                                                <IncrementalQuantity
                                                    id='ms-buybox__product-quantity-input'
                                                    min={1}
                                                    max={maxQuantity}
                                                    currentCount={quantities[index]}
                                                    onChange={value => {
                                                        handleQuantityChange(index, value);
                                                        return true;
                                                    }}
                                                    productBehavior={
                                                        FullProducts.find(productResult => product.RecordId === productResult.RecordId)
                                                            ?.Behavior
                                                    }
                                                />
                                            )}
                                        </td>
                                        <td>
                                            {availabilities[index] === Availability.loading ? (
                                                <span className='ms-search-result-container__loading'></span>
                                            ) : (
                                                <span
                                                    className={`ms-search-result-container__availability-${
                                                        availabilities[index] === Availability.available
                                                            ? Availability.availableClass
                                                            : Availability.outOfStockClass
                                                    }`}
                                                    title={
                                                        availabilities[index] === Availability.available
                                                            ? resources.availabilityIndicatorAvailable
                                                            : resources.availabilityIndicatorOutOfStock
                                                    }
                                                ></span>
                                            )}
                                        </td>
                                        <td>
                                            <AddToCartComponent
                                                quantity={quantities[index]}
                                                id={id}
                                                context={context}
                                                typeName={typeName}
                                                addToCartText={resources.addToCartText}
                                                data={{ product: simpleProduct }}
                                                disabled={false}
                                                onError={error => {
                                                    const obj = error.cartActionResult?.validationResults?.find(
                                                        x =>
                                                            x.ErrorResourceId ===
                                                            'Microsoft_Dynamics_Commerce_Runtime_ItemQuantityViolatesMultiplicityOfConfiguredNumber'
                                                    );
                                                    const object: errorMessage = obj as errorMessage;

                                                    if (
                                                        object &&
                                                        object.LocalizedMessageParameters &&
                                                        object.LocalizedMessageParameters.length > 0
                                                    ) {
                                                        const param = object.LocalizedMessageParameters[0];
                                                        _showToast(
                                                            product.Name!,
                                                            props.resources.errorMessageForQtyValidation.replace('{param}', param)
                                                        );
                                                    }

                                                    const quantityError = error.cartActionResult?.validationResults?.find(
                                                        x =>
                                                            x.ErrorResourceId ===
                                                            'Microsoft_Dynamics_Commerce_Runtime_ItemQuantityViolatesMultiplicityOfConfiguredNumber'
                                                    );

                                                    const addToCartError = error.cartActionResult?.validationResults?.find(
                                                        x => x.ErrorResourceId === 'RequiredAttribute_ValidationError'
                                                    );

                                                    if (quantityError) {
                                                        const object: errorMessage = quantityError as errorMessage;
                                                        if (
                                                            object.LocalizedMessageParameters &&
                                                            object.LocalizedMessageParameters.length > 0
                                                        ) {
                                                            const param = object.LocalizedMessageParameters[0];
                                                            _showToast(
                                                                product.Name!,
                                                                props.resources.errorMessageForQtyValidation.replace('{param}', param)
                                                            );
                                                        }
                                                    } else if (addToCartError) {
                                                        _showToast(props.resources.errorMessageForAddToCart, '');
                                                    } else {
                                                        const validationResults = error.cartActionResult?.validationResults;
                                                        const plainResults: DataValidationFailure[] = validationResults
                                                            ? toJS(validationResults)
                                                            : [];
                                                        if (plainResults.length > 0 && plainResults[0].LocalizedMessage) {
                                                            _showToast(plainResults[0].LocalizedMessage, '');
                                                        }
                                                    }
                                                }}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Node>
        </>
    );
};

const fetchAvailableQuantity = async (product: ProductSearchResult, context: ICoreContext): Promise<AsyncResult<any> | null> => {
    try {
        const availability = await getProductAvailabilitiesForSelectedVariant(
            new ProductAvailabilitiesForSelectedVariantInput(
                Number(product.RecordId),
                Number(context.actionContext.requestContext.channel?.ChannelNaturalId)
            ),
            context.actionContext
        );
        return availability;
    } catch (error) {
        return null;
    }
};

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProducts = (props: ISearchResultContainerViewProps & ISearchResultContainerProps<{}>): JSX.Element | null => {
    const products = props.data.listPageState.result?.activeProducts;
    if (!products || !ArrayExtensions.hasElements(products)) {
        return null;
    }
    return <Node {...props.ProductsContainer}>{renderProductsAsTable(props)}</Node>;
};

/**
 * Render Category Hierarchy.
 * @param props - The category hierarchy view props.
 * @returns The JSX Element.
 */
const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
    const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = props;
    if (categoryHierarchyList) {
        return (
            <Node {...CategoryHierarchyContainer}>
                {categoryHierarchyList.map((category, index) => (
                    <React.Fragment key={index}>
                        {category}
                        {categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                    </React.Fragment>
                ))}
            </Node>
        );
    }

    return null;
};

/**
 * Function to make filter sticky.
 * @param isMobile - The boolean prop.
 * @param isStickyHeader - The boolean prop.
 */
export const configureStickyFilter = (isMobile: boolean, isStickyHeader: boolean): void => {
    // Get heights of cookie and promotion banners
    const defaultValue = 0;
    const defaultOffset = 28;
    const headerContainer: HTMLElement | null = document.querySelector('.ms-header');

    const headerHeight: number | undefined = headerContainer?.offsetHeight;
    const containerHeight: number = headerHeight ?? defaultOffset;
    const bannerHeights = isStickyHeader ? defaultValue : containerHeight;
    const headerElement = document.querySelector('header .lock-opaque');
    const defaultContainer = document.querySelector('header .default-container');
    const refinerElement = isMobile ? document.querySelector('.msc-sort-filter-wrapper') : document.querySelector('.ms-refiner-heading');
    if (refinerElement && headerElement && defaultContainer) {
        if (MsDyn365.isBrowser && document.documentElement.scrollTop > bannerHeights) {
            refinerElement.classList.add('lock-Filter');
            refinerElement.setAttribute('style', `top: ${defaultContainer.clientHeight}px`);
        } else {
            refinerElement.classList.remove('lock-Filter');
        }
    } else if (refinerElement) {
        if (MsDyn365.isBrowser && document.documentElement.scrollTop > bannerHeights) {
            refinerElement.classList.add('lock-top');
        } else {
            refinerElement.classList.remove('lock-top');
            refinerElement.classList.remove('lock-Filter');
            refinerElement.removeAttribute('style');
        }
    }
};

/**
 * Function to setup filter sticky.
 * @param isMobile - The boolean prop.
 * @param isStickyHeader - The boolean prop.
 */
function stickyFilterSetup(isMobile: boolean, isStickyHeader: boolean): void {
    window.addEventListener('scroll', () => {
        configureStickyFilter(isMobile, isStickyHeader);
    });
    configureStickyFilter(isMobile, isStickyHeader);
}

/**
 * Function to make filter sticky.
 * @param refinerRefernce - Ref prop for filter.
 * @param toggle - Toggle function for filter menu.
 */
function useOutsideClick(refinerRefernce: React.RefObject<HTMLElement>, toggle: (event: Event) => void) {
    React.useEffect(() => {
        /**
         * Function to make filter sticky.
         * @param event - Event prop for filter.
         */
        function handleClickOutside(event: Event) {
            const element = document.querySelector('.ms-refine-filter__toggle_collapsed');
            if (refinerRefernce.current && !refinerRefernce.current.contains(event.target as Node) && !element) {
                toggle(event);
            }
        }
        if (MsDyn365.isBrowser) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => {
            if (MsDyn365.isBrowser) {
                document.removeEventListener('click', handleClickOutside);
            }
        };
    }, [refinerRefernce, toggle]);
}

/**
 * Render the category page items.
 * @param props - The view props.
 * @returns The search Result container module view.
 */
const SearchResultContainerView: React.FC<ISearchResultContainerViewProps & ISearchResultContainerProps<{}>> = props => {
    const {
        SearchResultContainer,
        pagination,
        ProductSectionContainer,
        choiceSummary,
        isMobile,
        modalToggle,
        searchResultModal,
        TitleViewProps,
        refineMenu,
        categoryHierarchy,
        sortByOptions,
        CategoryNavContainer,
        RefineAndProductSectionContainer,
        FeatureSearchContainer,
        similarLookProduct,
        errorMessage
    } = props;
    useEffect(() => {
        if (props.config.useStickyFilter && MsDyn365.isBrowser) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app context is generic
            stickyFilterSetup(props.isMobile, props.context.app.config.enableStickyHeader as boolean);
        }
    }, [props]);
    const [openRefinerIndex, setOpenRefinerIndex] = useState<number | null>(null);
    console.log('openRefinerIndex', openRefinerIndex);

    const wrapperRefiner = React.useRef(null);
    useOutsideClick(wrapperRefiner, () => setOpenRefinerIndex(null));
    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;
    if (isMobile) {
        return (
            <Module {...SearchResultContainer}>
                {similarLookProduct ? null : (
                    <Node {...CategoryNavContainer}>
                        {renderCategoryHierarchy(categoryHierarchy)}
                        {renderTitle(TitleViewProps)}
                    </Node>
                )}
                {choiceSummary}
                <Node className='msc-sort-filter-wrapper'>{modalToggle}</Node>
                {createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage)}
                <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                {errorMessage}
                {props.config.enableProductDescription ? renderProducts(props) : renderProductsAsTable(props)}
                {pagination}
            </Module>
        );
    }
    return (
        <Module {...SearchResultContainer}>
            {similarLookProduct ? null : (
                <Node {...CategoryNavContainer}>
                    {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
                    {TitleViewProps && renderTitle(TitleViewProps)}
                </Node>
            )}
            <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
            <Node {...RefineAndProductSectionContainer}>
                <Node ref={wrapperRefiner} className='ms-refiner-heading'>
                    <Node className='ms-refiner-heading_title'>
                        <div className='ms-refiner-chips'>
                            {refineMenu?.refiners?.map((submenu, index) => {
                                if (!submenu) {
                                    return null;
                                }
                                return (
                                    <div key={index} className='ms-refiner-chip'>
                                        {React.isValidElement(submenu)
                                            ? React.cloneElement(submenu as React.ReactElement, {
                                                  isOpen: openRefinerIndex === index,
                                                  onRequestToggle: () => setOpenRefinerIndex(prev => (prev === index ? null : index))
                                              })
                                            : submenu}
                                    </div>
                                );
                            })}
                        </div>
                        <div className='ms-search-result-wrapper-title-choice-summary'>{choiceSummary}</div>
                        <div className='ms-search-result-wrapper-sort-by-category'>
                            {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)}
                        </div>
                    </Node>
                </Node>

                <Node {...ProductSectionContainer}>
                    {errorMessage}
                    {props.config.enableProductDescription ? renderProducts(props) : renderProductsAsTable(props)}
                    {pagination}
                </Node>
            </Node>
        </Module>
    );
};

/**
 * Render Search result Modal.
 * @param modalProps - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createSearchResultModal = (
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element => {
    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, sortByDropDown, isRecoSearchPage),
        modalProps.modalFooter
    );
};

/**
 * Render Refiner Mobile.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefinerMobile = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render Search result Body.
 * @param props - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createModalBody = (
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(props.modalBody, {}, renderSort(sortByDropDown, isRecoSearchPage), renderRefinerMobile(refineMenu));
    }
    return null;
};

/**
 * Render sort by drop down.
 * @param props - The sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;
    if (sortByDropDown && !isRecoSearchPage) {
        return <Node {...SortingContainer}>{sortByDropDown}</Node>;
    }
    return null;
};

/**
 * Render Title.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <Node tag='h2' className=''>
                    {title.titlePrefix}
                    {title.titleText}
                    <small>&mdash; {title.titleCount}</small>
                </Node>
            </Node>
        );
    }
    return null;
};

export default SearchResultContainerView;
